<template>
  <div>
    <div class="d-flex flex-wrap align-items-center mb-3" style="gap: 8px;">
      <div class="flex-grow-1">
        <h5 class="mb-0 font-weight-bold">Enrolment targets</h5>
        <small class="text-muted">Progress per programme against a straight line from the start to the target, and where the enrolment forecast says each target will end up.</small>
      </div>
      <select class="form-control form-control-sm" style="width: auto;" v-model="show" aria-label="Show">
        <option value="all">All targets</option><option value="active">Running now</option><option value="ended">Ended</option>
      </select>
      <button v-if="canManage" class="btn btn-sm btn-submit" @click="openForm(null)"><i class="fe fe-plus mr-1"></i> New target</button>
    </div>

    <div v-if="loading && !targets.length" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
    <div v-else-if="!visible.length" class="card shadow-sm"><div class="card-body empty-state">
      <i class="fe fe-target"></i>{{ targets.length ? "No targets match." : "No targets yet." }}
      <div v-if="canManage && !targets.length" class="mt-2"><button class="btn btn-sm btn-outline-primary" @click="openForm(null)">Set the first target</button></div>
    </div></div>

    <div class="row">
      <div class="col-lg-6 mb-3" v-for="t in visible" :key="t.id">
        <div class="card shadow-sm h-100 target-card" :class="{ selected: selected && selected.id === t.id }">
          <div class="card-body">
            <div class="d-flex align-items-start" style="gap: 8px;">
              <div class="flex-grow-1">
                <h6 class="mb-0 font-weight-bold">{{ t.name }}</h6>
                <small class="text-muted">{{ t.sector || "All programmes" }} · {{ kindLabel(t) }} · {{ date(t.start_date) }} – {{ date(t.end_date) }}</small>
              </div>
              <span class="status-pill" :class="'st-' + t.status">{{ t.status_label }}</span>
            </div>

            <template v-if="t.actual !== undefined">
              <div class="d-flex justify-content-between align-items-end mt-3">
                <div><span class="stat-label">Now</span><div class="stat-value">{{ fmt(t.actual) }}</div></div>
                <div class="text-right"><span class="stat-label">Target</span><div class="stat-value">{{ fmt(t.target) }}<small v-if="t.kind === 'coverage'" class="text-muted"> ({{ t.target_value }}%)</small></div></div>
              </div>
              <div class="target-bar mt-2" :title="`Expected by now: ${fmt(t.expected_now)}`">
                <div class="fill" :class="'st-' + t.status" :style="{ width: Math.min(100, t.achieved_pct) + '%' }"></div>
                <div class="marker" :style="{ left: Math.min(100, (100 * t.expected_now) / t.target) + '%' }"></div>
              </div>
              <div class="d-flex justify-content-between small text-muted mt-1">
                <span>{{ t.achieved_pct }}% of target</span>
                <span>{{ t.time_elapsed_pct }}% of the time gone</span>
              </div>

              <div class="mini-stats mt-3">
                <div v-if="t.kind !== 'new'"><span class="stat-label">Growth since start</span>
                  <div>{{ signed(t.growth) }}<span v-if="t.growth_pct !== null" class="text-muted"> ({{ signedPct(t.growth_pct) }} of {{ signedPct(t.growth_needed_pct) }} needed)</span></div></div>
                <div><span class="stat-label">{{ t.gap_to_expected >= 0 ? "Ahead of plan by" : "Behind plan by" }}</span><div :class="t.gap_to_expected >= 0 ? 'text-success' : 'text-danger'">{{ fmt(Math.abs(t.gap_to_expected)) }}</div></div>
                <div v-if="t.required_per_month !== null"><span class="stat-label">Needed per month</span><div>{{ fmt(t.required_per_month) }} <span class="text-muted">vs {{ fmt(Math.round(t.recent_per_month)) }} recently</span></div></div>
                <div v-if="t.projection"><span class="stat-label">Forecast at end date</span>
                  <div :class="t.projection.value >= t.target ? 'text-success' : 'text-danger'">{{ fmt(t.projection.value) }} <span class="text-muted">({{ fmt(t.projection.low) }}–{{ fmt(t.projection.high) }})</span></div></div>
                <div v-if="t.coverage_now_pct !== null"><span class="stat-label">Coverage now</span><div>{{ t.coverage_now_pct }}% of {{ fmt(t.population) }}</div></div>
              </div>
            </template>
            <div v-else class="alert alert-warning small py-2 mt-3 mb-0">{{ t.message }} <router-link to="/population">Population page</router-link></div>
            <p v-if="t.notes" class="small text-muted mt-2 mb-0">{{ t.notes }}</p>

            <div class="d-flex mt-3" style="gap: 6px;">
              <button v-if="t.actual !== undefined" class="btn btn-sm btn-outline-primary" @click="select(t)"><i class="fe fe-trending-up mr-1"></i> Chart</button>
              <button v-if="canManage" class="btn btn-sm btn-outline-secondary" @click="openForm(t)"><i class="fe fe-edit-2"></i></button>
              <button v-if="canManage" class="btn btn-sm btn-outline-danger" @click="remove(t)"><i class="fe fe-trash-2"></i></button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="selected" class="card shadow-sm table-card mb-4" ref="chart">
      <div class="form-card-header">
        <div class="section-number"><i class="fe fe-trending-up" style="font-size: 12px;"></i></div>
        <div class="flex-grow-1">
          <h6 class="mb-0 font-weight-bold text-white">{{ selected.name }}</h6>
          <small style="color: rgba(255,255,255,.75);">{{ selected.kind === "new" ? "Cumulative new enrolments" : "Enrollees covered" }} by month: actual, the straight path to the target, and the forecast to the end date</small>
        </div>
        <button class="btn btn-sm btn-light" @click="selected = null"><i class="fe fe-x"></i></button>
      </div>
      <div class="card-body">
        <div v-if="!detail" class="empty-state"><i class="mdi mdi-loading mdi-spin"></i>Loading…</div>
        <template v-else>
          <apexchart type="line" height="340" :options="chartOptions" :series="chartSeries" :key="chartKey" />
          <p class="small text-muted mb-0">
            The shaded lines are the forecast's 95% range ({{ detail.projection ? detail.projection.method : "" }}). "Covered on a date" counts people enrolled by then who are still enabled today.
          </p>
        </template>
      </div>
    </div>

    <!-- form -->
    <div v-if="editing" class="nr-backdrop" @click.self="editing = false">
      <div class="nr-dialog">
        <div class="nr-head"><h5 class="mb-0">{{ draft.id ? "Edit target" : "New enrolment target" }}</h5>
          <button class="btn btn-sm btn-link text-secondary" @click="editing = false"><i class="fe fe-x"></i></button></div>
        <div class="nr-body">
          <div class="form-group"><label class="small font-weight-bold text-secondary">Name</label>
            <input class="form-control form-control-sm" v-model="draft.name" maxlength="150" placeholder="e.g. Informal sector 2027" /></div>
          <div class="form-row">
            <div class="form-group col-md-6"><label class="small font-weight-bold text-secondary">What is measured</label>
              <select class="form-control form-control-sm" v-model="draft.kind">
                <option value="total">Enrollees covered by the end date</option>
                <option value="new">New enrolments during the period</option>
                <option value="coverage" :disabled="!population">% of the population covered{{ population ? "" : " (needs population)" }}</option>
              </select></div>
            <div class="form-group col-md-6"><label class="small font-weight-bold text-secondary">Programme</label>
              <select class="form-control form-control-sm" v-model="draft.sector" :disabled="draft.kind === 'coverage'">
                <option :value="null">All programmes</option>
                <option v-for="s in sectorOptions" :key="s" :value="s">{{ s }}</option>
              </select></div>
          </div>
          <div class="form-row">
            <div class="form-group col-md-4"><label class="small font-weight-bold text-secondary">{{ draft.kind === "coverage" ? "Target (% of population)" : "Target (people)" }}</label>
              <input type="number" min="1" :max="draft.kind === 'coverage' ? 100 : undefined" step="any" class="form-control form-control-sm" v-model.number="draft.target_value" />
              <small v-if="draft.kind === 'coverage' && population && draft.target_value" class="text-muted">≈ {{ fmt(Math.round((draft.target_value / 100) * population)) }} people</small></div>
            <div class="form-group col-md-4"><label class="small font-weight-bold text-secondary">From</label>
              <input type="date" class="form-control form-control-sm" v-model="draft.start_date" /></div>
            <div class="form-group col-md-4"><label class="small font-weight-bold text-secondary">To</label>
              <input type="date" class="form-control form-control-sm" v-model="draft.end_date" /></div>
          </div>
          <div class="form-group mb-0"><label class="small font-weight-bold text-secondary">Notes</label>
            <textarea class="form-control form-control-sm" rows="2" v-model="draft.notes" maxlength="2000"></textarea></div>
        </div>
        <div class="nr-foot">
          <button class="btn btn-sm btn-outline-secondary" @click="editing = false">Cancel</button>
          <button class="btn btn-sm btn-submit" :disabled="saving || !draft.name || !draft.target_value || !draft.start_date || !draft.end_date" @click="save">{{ saving ? "Saving…" : "Save" }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";

/** Enrolment targets per programme with progress and forecast projection (GET/POST/PUT/DELETE targets). */
export default {
  name: "TargetsPanel",
  data() {
    return {
      targets: [], canManage: false, population: null, loading: false, show: "all",
      selected: null, detail: null, chartKey: 0, editing: false, saving: false, draft: {}, sectorOptions: [],
    };
  },
  computed: {
    visible() {
      const today = moment().format("YYYY-MM-DD");
      if (this.show === "active") return this.targets.filter((t) => t.start_date <= today && t.end_date >= today);
      if (this.show === "ended") return this.targets.filter((t) => t.end_date < today);
      return this.targets;
    },
    chartSeries() {
      const s = this.detail.series;
      return [
        { name: "Actual", data: s.map((p) => p.actual) },
        { name: "Path to target", data: s.map((p) => p.target) },
        { name: "Forecast", data: s.map((p) => p.projected) },
        { name: "Forecast high", data: s.map((p) => p.projected_high) },
        { name: "Forecast low", data: s.map((p) => p.projected_low) },
      ];
    },
    chartOptions() {
      const d = this.detail;
      return {
        chart: { toolbar: { show: true }, zoom: { enabled: false } },
        colors: ["#1e3a8a", "#16a34a", "#7c3aed", "#c4b5fd", "#c4b5fd"],
        stroke: { width: [3, 2, 3, 1, 1], dashArray: [0, 5, 6, 3, 3], curve: "straight" },
        xaxis: { categories: d.series.map((p) => p.label), labels: { rotate: -45, hideOverlappingLabels: true } },
        yaxis: { labels: { formatter: (v) => this.fmt(Math.round(v)) } },
        annotations: { yaxis: [{ y: d.target, borderColor: "#16a34a", label: { text: `Target ${this.fmt(d.target)}`, style: { background: "#16a34a", color: "#fff" } } }] },
        markers: { size: [3, 0, 0, 0, 0] },
        dataLabels: { enabled: true, enabledOnSeries: [0, 2] },
        legend: { position: "top" },
        tooltip: { shared: true, intersect: false, y: { formatter: (v) => (v === null || v === undefined ? "—" : this.fmt(v)) } },
      };
    },
  },
  mounted() {
    this.load();
    this.$batchGet("/api/v1/auth/analytics/enrollment/sectors").then((res) => {
      this.sectorOptions = (res.data.data.rows || []).map((r) => r.sector).filter((s) => s && s !== "Not set");
    }).catch(() => {});
  },
  methods: {
    fmt(v) { return v === null || v === undefined ? "—" : Number(v).toLocaleString(); },
    signed(v) { return (v > 0 ? "+" : "") + this.fmt(v); },
    signedPct(v) { return v === null || v === undefined ? "—" : (v > 0 ? "+" : "") + v + "%"; },
    date(d) { return moment(d).format("D MMM YYYY"); },
    kindLabel(t) { return { total: "Enrollees by end date", new: "New enrolments", coverage: "Population coverage" }[t.kind]; },
    load() {
      this.loading = true;
      this.axios.get("/api/v1/auth/targets").then((res) => {
        this.targets = res.data.data;
        this.canManage = res.data.can_manage;
        this.population = res.data.population;
      }).catch(() => {}).finally(() => { this.loading = false; });
    },
    select(t) {
      this.selected = t;
      this.detail = null;
      this.axios.get(`/api/v1/auth/targets/${t.id}`).then((res) => { this.detail = res.data.data; this.chartKey++; }).catch(() => {});
      this.$nextTick(() => this.$refs.chart && this.$refs.chart.scrollIntoView({ behavior: "smooth" }));
    },
    openForm(t) {
      this.draft = t
        ? { id: t.id, name: t.name, kind: t.kind, sector: t.sector || null, target_value: t.target_value, start_date: t.start_date, end_date: t.end_date, notes: t.notes }
        : { name: "", kind: "total", sector: null, target_value: null, start_date: moment().startOf("year").format("YYYY-MM-DD"), end_date: moment().endOf("year").format("YYYY-MM-DD"), notes: "" };
      this.editing = true;
    },
    async save() {
      this.saving = true;
      try {
        const body = { ...this.draft, sector: this.draft.kind === "coverage" ? null : this.draft.sector };
        const res = this.draft.id ? await this.axios.put(`/api/v1/auth/targets/${this.draft.id}`, body) : await this.axios.post("/api/v1/auth/targets", body);
        this.$toasted.success(res.data.message, { position: "top-center", duration: 3000 });
        this.editing = false;
        this.load();
        if (this.selected && this.selected.id === this.draft.id) this.select(res.data.data);
      } catch (e) {
        const d = e.response && e.response.data;
        this.$toasted.error((d && d.errors && Object.values(d.errors).flat()[0]) || (d && d.message) || "Could not save the target.", { position: "top-center", duration: 4000 });
      } finally {
        this.saving = false;
      }
    },
    remove(t) {
      if (!window.confirm(`Delete the target "${t.name}"?`)) return;
      this.axios.delete(`/api/v1/auth/targets/${t.id}`).then(() => {
        if (this.selected && this.selected.id === t.id) this.selected = null;
        this.load();
      }).catch(() => this.$toasted.error("Could not delete the target.", { position: "top-center", duration: 4000 }));
    },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
.target-card.selected { border: 2px solid #1f6feb; }
.status-pill { font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 999px; white-space: nowrap; }
.status-pill.st-achieved, .status-pill.st-on_track { background: #dcfce7; color: #166534; }
.status-pill.st-at_risk { background: #fef3c7; color: #92400e; }
.status-pill.st-behind, .status-pill.st-missed { background: #fee2e2; color: #991b1b; }
.status-pill.st-not_started, .status-pill.st-no_population { background: #e2e8f0; color: #334155; }
.target-bar { position: relative; height: 12px; background: #e2e8f0; border-radius: 6px; }
.target-bar .fill { height: 100%; border-radius: 6px; background: #1f6feb; }
.target-bar .fill.st-achieved, .target-bar .fill.st-on_track { background: #16a34a; }
.target-bar .fill.st-at_risk { background: #f59e0b; }
.target-bar .fill.st-behind, .target-bar .fill.st-missed { background: #dc2626; }
.target-bar .marker { position: absolute; top: -4px; width: 2px; height: 20px; background: #0f172a; }
.mini-stats { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 10px; font-size: 13px; }
.nr-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 1060; display: flex; align-items: flex-start; justify-content: center; padding: 48px 16px; overflow-y: auto; }
.nr-dialog { background: #fff; border-radius: 12px; width: 100%; max-width: 640px; box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25); }
.nr-head { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; }
.nr-body { padding: 16px 20px; }
.nr-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 20px; border-top: 1px solid #e2e8f0; }
</style>
